import type { Callout, CalloutAnchor, CalloutArrow, CalloutPositionMode, CalloutTheme } from "./types";

export const MIN_CALLOUT_WIDTH = 220;
export const MAX_CALLOUT_WIDTH = 800;
export const DEFAULT_CALLOUT_WIDTH = 320;

/**
 * The screen width a callout card is designed for. Its width, text and padding are px of a screen this wide, and every
 * view (the Studio at any zoom, Quick Preview, Preview, the published view) scales the card by the width it draws the
 * screen at / this, so the card covers the same part of the page everywhere (src/calloutScale.ts).
 */
export const CALLOUT_REF_WIDTH = 1440;

/**
 * Where an arrow's tip is, from the card, in the card's own px: this far along the side it is on (from the card's left
 * for a top or bottom arrow, from its top for a left or right one) and this far out from that side. The arrow is
 * .callout-pointer in src/styles.css: a 14px square turned 45 degrees, its centre 31px along and 1px out.
 */
export const CALLOUT_TIP_ALONG = 31;
export const CALLOUT_TIP_OUT = 7 * Math.SQRT2 + 1;
/** How far an attached callout's anchor numbers may go, in page px (a long page, scrolled far). */
export const MAX_ANCHOR_PX = 1_000_000;

/** A callout that points somewhere: an arrow, and not a center-stage modal (which ignores where it was put). */
export function hasArrow(co: Pick<Callout, "arrow" | "modal">): boolean {
  return !!co.arrow && co.arrow !== "none" && !co.modal;
}

/**
 * Attached to the element its arrow points at: its card hangs off its arrow's tip, wherever that element is (see
 * CalloutAnchor). One with an arrow but no anchor (saved before this) sits at its x/y, as before.
 */
export function isAttached(co: Pick<Callout, "arrow" | "modal" | "anchor">): boolean {
  return hasArrow(co) && !!co.anchor;
}

type Pt = [number, number];
/** A card on the screen, in page px. */
export type CardBox = { x: number; y: number; w: number; h: number };

/** Where the tip of `arrow` is on a card at `card`; `k` is page px per card px (the card's scale). */
export function tipOf(arrow: CalloutArrow, card: CardBox, k: number): Pt {
  const along = CALLOUT_TIP_ALONG * k, out = CALLOUT_TIP_OUT * k;
  switch (arrow) {
    case "bottom": return [card.x + along, card.y + card.h + out];
    case "left": return [card.x - out, card.y + along];
    case "right": return [card.x + card.w + out, card.y + along];
    default: return [card.x + along, card.y - out];
  }
}

/** Where a card of size w x h goes for its `arrow`'s tip to be at `tip`: its top-left corner (the inverse of tipOf). */
export function cardAt(arrow: CalloutArrow, tip: Pt, w: number, h: number, k: number): Pt {
  const [x, y] = tipOf(arrow, { x: 0, y: 0, w, h }, k);
  return [tip[0] - x, tip[1] - y];
}

/**
 * Where an arrow let go at `tip`, over an element at `el`, snaps to: the element's edge facing the card (the card sits
 * on the arrow's side: a top arrow's card hangs below the element, pointing up at its bottom edge), keeping the tip's
 * place along that edge. An element bigger than 40% of the screen across that edge (a panel, the page) is pointed
 * into, not at its edge: the tip stays where it was let go.
 */
export function snapTip(arrow: CalloutArrow, tip: Pt, el: CardBox, screen: { w: number; h: number }): Pt {
  const vertical = arrow === "top" || arrow === "bottom";
  if (vertical ? el.h > screen.h * 0.4 : el.w > screen.w * 0.4) return tip;
  switch (arrow) {
    case "bottom": return [tip[0], el.y];
    case "left": return [el.x + el.w, tip[1]];
    case "right": return [el.x, tip[1]];
    default: return [tip[0], el.y + el.h];
  }
}

export function sanitizeAnchor(raw: unknown): CalloutAnchor | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  const out: Partial<CalloutAnchor> = {};
  for (const k of ["px", "py", "sx", "sy", "ox", "oy"] as const) {
    const v = a[k];
    if (typeof v !== "number" || !isFinite(v) || Math.abs(v) > MAX_ANCHOR_PX) return null;
    out[k] = Math.round(v * 100) / 100;
  }
  return out as CalloutAnchor;
}

export function clampCalloutWidth(width: number | null | undefined): number | null {
  if (width == null) return null;
  if (typeof width !== "number" || isNaN(width)) return null;
  return Math.min(MAX_CALLOUT_WIDTH, Math.max(MIN_CALLOUT_WIDTH, Math.round(width)));
}

/**
 * Where a callout may sit, as a percentage of the capture's width / height. A "Stays on Screen" callout is
 * always within 0-100 (the screen). A "Moves with Page" callout is at a spot on the page, measured from the
 * top-left of the page's scrolling content, so it can be further down (or right) than the first screen:
 * 250 is two and a half screens down.
 */
export const MAX_CALLOUT_PCT = 5000;

/**
 * The two ways a callout can sit, and nothing else:
 *  - Moves with Page (`pinned`): at a fixed spot ON THE PAGE. Scroll down and it comes into view; scroll
 *    back up and it is still there. Its x/y are page coordinates (see MAX_CALLOUT_PCT).
 *  - Stays on Screen (`sticky`): at a fixed spot on the screen, however the page scrolls or the camera
 *    zooms. Its x/y are screen coordinates, 0-100.
 * A center-stage modal always stays on screen. This is true for Stays on Screen and for any modal.
 */
export function staysOnScreen(co: Pick<Callout, "positionMode" | "modal">): boolean {
  return co.positionMode === "sticky" || !!co.modal;
}

/**
 * Whether a Moves-with-Page callout is still in the old coordinates: measured from the screen as the page was
 * last saved, rather than from the top-left of the page's scrolling content. To place it, the scroll position
 * the page opens at (its saved one) is added to x/y; the editor does that once and saves it as page coordinates.
 */
export function isLegacyPinned(co: Pick<Callout, "positionMode" | "modal" | "pageCoords">): boolean {
  return !staysOnScreen(co) && !co.pageCoords;
}

export function sanitizeCallout(raw: Partial<Callout> | null | undefined): Callout | null {
  if (!raw || typeof raw !== "object") return null;
  const x = typeof raw.x === "number" && !isNaN(raw.x) ? Math.min(MAX_CALLOUT_PCT, Math.max(0, raw.x)) : 6;
  const y = typeof raw.y === "number" && !isNaN(raw.y) ? Math.min(MAX_CALLOUT_PCT, Math.max(0, raw.y)) : 8;
  const arrow: CalloutArrow = ["none", "top", "bottom", "left", "right"].includes(raw.arrow as any)
    ? (raw.arrow as CalloutArrow)
    : "none";
  const theme: CalloutTheme = ["default", "dark", "accent"].includes(raw.theme as any)
    ? (raw.theme as CalloutTheme)
    : "default";
  const positionMode: CalloutPositionMode = raw.positionMode === "sticky" ? "sticky" : "pinned";
  const modal = Boolean(raw.modal);
  const width = clampCalloutWidth(raw.width);
  const anchor = sanitizeAnchor(raw.anchor);

  return {
    title: typeof raw.title === "string" ? raw.title : "",
    body: typeof raw.body === "string" ? raw.body : "",
    button: typeof raw.button === "string" ? raw.button : "",
    x,
    y,
    arrow,
    modal,
    width,
    theme,
    positionMode,
    pageCoords: raw.pageCoords === true,
    anchor,
  };
}
